import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Modal } from "./Modal";
import { Button } from "./Button";
import { Input, FormField, Select } from "./Form";
import { MEDIA } from "../../utils/media";
import { useToast } from "./Toast";
import { redirectsApi } from "../../services/toolsApi";

/**
 * EditUtmModal
 *
 * Admin control to set a campaign's UTM parameters (utm_medium / utm_content).
 * Mirrors the legacy dashboard's UTM editor. utm_medium is normally
 * auto-calculated from the dominant source; setting it here overrides that.
 * Clearing utm_medium reverts to auto.
 *
 * Calls redirectsApi.updateCampaignUtm → /api/updateCampaignUtm.
 */
const EditUtmModal = ({
  isOpen,
  onClose,
  campaignId,
  displayName,
  currentMedium = "",
  currentContent = "",
  mediumOverride = false,
  onSuccess,
}) => {
  const toast = useToast();
  const [medium, setMedium] = useState("");
  const [content, setContent] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMedium(currentMedium || "");
      setContent(currentContent || "");
    }
  }, [isOpen, currentMedium, currentContent]);

  const persist = async (fields, successMsg) => {
    setIsLoading(true);
    try {
      const result = await redirectsApi.updateCampaignUtm(campaignId, fields);
      if (result && result.success !== false) {
        toast.success(successMsg);
        if (onSuccess) onSuccess();
        onClose();
      } else {
        throw new Error(result?.error || "Update failed");
      }
    } catch (error) {
      console.error("Error updating UTM:", error);
      toast.error(error.message || "Failed to update UTM parameters", {
        debugInfo: {
          operation: "updateCampaignUtm",
          params: { campaignId, ...fields },
          error: error.stack || error.toString(),
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Only what was CHANGED is sent (the review of PR 196, LOW-3). Sending the medium with every save stored whatever
  // the box opened with: a medium off the list was then refused, and a guess was saved as if set by hand.
  const handleSave = () => {
    const fields = {};
    if (medium.trim() !== (currentMedium || "")) fields.utmMedium = medium.trim() || null;
    if (content.trim() !== (currentContent || "")) fields.utmContent = content.trim() || null;
    if (!Object.keys(fields).length) { onClose(); return; }
    persist(fields, "UTM parameters updated");
  };

  const handleRevertMedium = () =>
    persist({ utmMedium: null }, "utm_medium reverted to auto-calculated");

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Edit UTM — ${displayName || campaignId}`}
      size="small"
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <FormField
          label="utm_medium"
          helperText="One of the fixed list, or the guess (from the link's type)."
        >
          <Select
            value={medium}
            onChange={(e) => setMedium(e.target.value)}
            placeholder=""
            options={[{ value: "", label: "The guess" }, ...MEDIA.map((m) => ({ value: m, label: m })),
              ...(medium && !MEDIA.includes(medium) ? [{ value: medium, label: `${medium} (not on the list)`, disabled: true }] : [])]}
            disabled={isLoading}
            fullWidth
          />
        </FormField>

        <FormField
          label="utm_content"
          helperText="Optional extra UTM content tag for this campaign."
        >
          <Input
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="(none)"
            disabled={isLoading}
          />
        </FormField>

        {/* Preview of resulting tracking params */}
        <div
          style={{
            background: "#f8fafc",
            border: "1px solid #e5e7eb",
            borderRadius: 6,
            padding: "8px 10px",
            fontSize: "0.75rem",
            color: "#1e3a8a",
            wordBreak: "break-all",
          }}
        >
          ?utm_campaign={campaignId}&amp;utm_source=&lt;source&gt;
          &amp;utm_medium={medium.trim() || "<auto>"}
          {content.trim() ? `&utm_content=${content.trim()}` : ""}
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          {mediumOverride && (
            <Button
              variant="ghost"
              onClick={handleRevertMedium}
              disabled={isLoading}
              title="Clear the manual medium and revert to auto-calculated"
            >
              Revert to auto
            </Button>
          )}
          <Button variant="primary" onClick={handleSave} disabled={isLoading}>
            {isLoading ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

EditUtmModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  campaignId: PropTypes.string,
  displayName: PropTypes.string,
  currentMedium: PropTypes.string,
  currentContent: PropTypes.string,
  mediumOverride: PropTypes.bool,
  onSuccess: PropTypes.func,
};

export default EditUtmModal;
